import React from 'react';
import {
    CompetitiveRoundSummaries,
    CompetitiveRoundSummariesDeathEventInner,
    CompetitiveRoundSummariesDeathEventInnerKillerLocation,
    CompetitiveRoundSummariesOtherAbilitiesInner,
    CompetitiveRoundSummariesSmokeAbilitiesInner,
    CompetitiveRoundSummariesUtilityAbilitiesInner,
    Maps
} from "../../reckon-api";
import Box from "@mui/material/Box";
import {Circle, ImageOverlay, MapContainer, Popup, Tooltip, useMap} from "react-leaflet";
import {CRS} from "leaflet";
import {useEffect, useMemo, useRef, useState} from "react";
import 'leaflet/dist/leaflet.css';
import "./Map.css";
import {CircularProgress} from "@mui/material";
import {attacker_color, defender_color} from "../../theme";
import UtilityAbilityOnMap from "./Markers/UtilityAbilityOnMap";
import PlayerOnMap from "./Markers/PlayerOnMap";
import {AbilityCastCard} from "./Markers/AbilityCastCard";
import {formatDuration} from "../../utils";
import {ValorantApi} from "../../valorant-api/api";
import {MapData} from "../../valorant-api/types";

// Extended types that include agent and side information
export type SmokeAbilityWithAgent = CompetitiveRoundSummariesSmokeAbilitiesInner & { agent: string; side: string };
export type UtilityAbilityWithAgent = CompetitiveRoundSummariesUtilityAbilitiesInner & { agent: string; side: string };
export type OtherAbilityWithAgent = CompetitiveRoundSummariesOtherAbilitiesInner & { agent: string; side: string; player?: string };
export type KillWithAgent = CompetitiveRoundSummariesDeathEventInner & { killer_agent: string; side: string };

/**
 * Project in-game XY onto the minimap.
 * Riot's formula (valorant-api): image left = gameY * xMult + xAdd, image top = gameX * yMult + yAdd.
 * Leaflet CRS.Simple uses [lat, lng] with lat increasing upward, so we return
 * { x: 1 - top, y: left } and callers pass [x * size, y * size] as [lat, lng].
 */
export function positionToMapBounds(position: {x:number, y:number}, map: Maps){
    const xMult = map.x_multiplier;
    const yMult = map.y_multiplier;
    const xAdd = map.x_scalar_to_add;
    const yAdd = map.y_scalar_to_add;
    if (xMult && yMult && xAdd != null && yAdd != null) {
        const left = position.y * xMult + xAdd;
        const top = position.x * yMult + yAdd;
        return {
            x: 1 - top,
            y: left,
        };
    }
    return {
        y: (position.y - map.min_y) / (map.max_y - map.min_y),
        x: (position.x - map.min_x) / (map.max_x - map.min_x),
    };
}
export const normalizeVector = (vector: { x: number; y: number; z: number }) => {
  const magnitude = Math.sqrt(vector.x ** 2 + vector.y ** 2 + vector.z ** 2);
  return magnitude > 0
    ? { x: vector.x / magnitude, y: vector.y / magnitude, z: vector.z / magnitude }
    : { x: 0, y: 0, z: 0 };
};

export const scaleVector = (vector: { x: number; y: number; z: number }, scale: number) => ({
  x: vector.x * scale,
  y: vector.y * scale,
  z: vector.z * scale,
});

export function useMapProjection(map: Maps): Maps {
    const [mapData, setMapData] = useState(undefined as MapData | undefined);
    useEffect(() => {
        if (!map.uuid) return;
        ValorantApi.getMap(map.uuid)
            .then((response) => {
                setMapData(response.data as MapData);
            });
    }, [map]);
    return useMemo((): Maps => {
        if (!mapData) return map;
        return {
            ...map,
            x_multiplier: mapData.xMultiplier ?? map.x_multiplier,
            y_multiplier: mapData.yMultiplier ?? map.y_multiplier,
            x_scalar_to_add: mapData.xScalarToAdd ?? map.x_scalar_to_add,
            y_scalar_to_add: mapData.yScalarToAdd ?? map.y_scalar_to_add,
        };
    }, [map, mapData]);
}

// SpecifiedPosition matches the shared location shape used by positions/casts
export type SpecifiedPosition = CompetitiveRoundSummariesDeathEventInnerKillerLocation;

function FitSimpleMap({ size }: { size: number }) {
    const map = useMap();
    useEffect(() => {
        map.invalidateSize({ animate: false });
        map.setView([size / 2, size / 2], 0, { animate: false });
    }, [map, size]);
    return null;
}

export function MapRender(props: { map: Maps;
    id: string; children?: React.ReactNode;
    playerPositions?: {round: CompetitiveRoundSummaries, position?: CompetitiveRoundSummariesDeathEventInnerKillerLocation }[]
    smokes: SmokeAbilityWithAgent[]
    kills: KillWithAgent[]
    utils: UtilityAbilityWithAgent[]
    spikePosition?: {x: number, y: number}
}) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerSize, setContainerSize] = useState(null as number | null);
    const projectionMap = useMapProjection(props.map);

    useEffect(() => {
        if (!containerRef.current) return;
        let primed = false;
        let debounce: number | undefined;
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (!entry.contentRect) continue;
                const size = Math.floor(Math.min(entry.contentRect.width, entry.contentRect.height)) + 1;
                const apply = () => setContainerSize((prev) => (prev && Math.abs(prev - size) < 2 ? prev : size));
                if (!primed) {
                    primed = true;
                    apply();
                    return;
                }
                window.clearTimeout(debounce);
                debounce = window.setTimeout(apply, 80);
            }
        });
        resizeObserver.observe(containerRef.current);
        return () => {
            window.clearTimeout(debounce);
            resizeObserver.disconnect();
        };
    }, []);

    const map_url = `https://res.cloudinary.com/xenesis/image/upload/c_scale,h_${containerSize},w_${containerSize}/valAssets/${props.map ? props.map.id : props.id}.png`;
    return (
        <Box
            ref={containerRef}
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            {containerSize ? (
            <Box sx={{ width: containerSize, height: containerSize, flexShrink: 0, position: 'relative' }}>
            <MapContainer
                className="debug-leaflet-map"
                style={{ width: containerSize, height: containerSize }}
                center={[containerSize / 2, containerSize / 2]}
                scrollWheelZoom={false}
                zoom={0}
                dragging={false}
                minZoom={0}
                maxZoom={0}
                attributionControl={false}
                zoomControl={false}
                doubleClickZoom={false}
                crs={CRS.Simple}
                maxBounds={[
                    [0, 0],
                    [containerSize, containerSize],
                ]}
                bounds={[
                    [0, 0],
                    [containerSize, containerSize],
                ]}
            >
                <FitSimpleMap size={containerSize} />
                <ImageOverlay
                    className="debug-map-asset"
                    url={map_url}
                    bounds={[
                        [0, 0],
                        [containerSize, containerSize],
                    ]}
                />
                {props.smokes.map((smoke, index) => (
                    <SmokeOnMap
                        key={index + 'smoke'}
                        smoke={smoke}
                        containerSize={containerSize}
                        map={projectionMap}
                    />
                ))}

                {props.utils.map((util, index) => <UtilityAbilityOnMap key={index} util={util} containerSize={containerSize} map={projectionMap}/>)}

                {props.playerPositions ? props.playerPositions.map((prs) =>
                <PlayerOnMap key={prs.round.player || prs.round.round_id} map={projectionMap} containerSize={containerSize} prs={prs}/>) : <></>
                }

                {/* Spike marker when planted */}
                {props.spikePosition && (() => {
                    const spikeNormalized = positionToMapBounds(props.spikePosition, projectionMap);
                    const spikeX = spikeNormalized.x * containerSize;
                    const spikeY = spikeNormalized.y * containerSize;
                    return (
                        <>
                            {/* Outer glow ring */}
                            <Circle
                                center={[spikeX, spikeY]}
                                radius={20}
                                pathOptions={{ 
                                    color: '#22C55E', 
                                    fillColor: '#22C55E', 
                                    fillOpacity: 0.2,
                                    weight: 2,
                                    dashArray: '4, 4'
                                }}
                            />
                            {/* Inner spike marker */}
                            <Circle
                                center={[spikeX, spikeY]}
                                radius={10}
                                pathOptions={{ 
                                    color: '#16A34A', 
                                    fillColor: '#22C55E', 
                                    fillOpacity: 0.9,
                                    weight: 3
                                }}
                            >
                                <Popup>
                                    <span>💣 SPIKE PLANTED</span>
                                </Popup>
                            </Circle>
                        </>
                    );
                })()}
            </MapContainer>
            </Box>
            ) : <CircularProgress/>}
        </Box>
    );
}

function SmokeOnMap(props: { smoke: SmokeAbilityWithAgent; containerSize: number; map: Maps }) {
    const { smoke, containerSize, map } = props;
    if (!smoke.landing_point) return null;
    const position_normalized = positionToMapBounds(smoke.landing_point as {x: number, y: number}, map);
    const center: [number, number] = [position_normalized.x * containerSize, position_normalized.y * containerSize];
    const smokeTime = smoke.cast_position?.time_in_round ?? 0;
    const smokeColor = smoke.side === "Attacker" ? attacker_color : defender_color;

    return (
        <Circle
            center={center}
            radius={25}
            pathOptions={{ color: smokeColor, fillColor: smokeColor, fillOpacity: 0.45 }}
        >
            <Tooltip direction="top" offset={[0, -10]} opacity={1} sticky className="ability-hover-tooltip">
                <AbilityCastCard
                    agent={smoke.agent}
                    slot={smoke.slot}
                    side={smoke.side}
                    kind="Smoke"
                    timeLabel={formatDuration(smokeTime)}
                    durationLabel={`${Math.round(smoke.duration)}s`}
                />
            </Tooltip>
        </Circle>
    );
}
